import * as moment from 'moment';

export interface DashboardSlot {
  productName: string;
  venueName: string;
  date: string;
  startDate?: string;
  endDate?: string;
  startTime?: string;
  endTime?: string;
  status: string;
}

export interface DashboardTransaction {
  bookingId: string;
  userName: string;
  userPhone?: string;
  bookingDateTime: string;
  bookingStatus: string;
  paymentStatus: string;
  paymentType: string;
  transactionId?: string;
  transactionStatus?: string;
  totalAmount: number;
  totalPlatformFeeEndUser?: number;
  discountAmount?: number;
  selectedDays?: string[];
  slotsBooked: { [group: string]: DashboardSlot[] };
}

export interface RevenuePeriod {
  key: string;
  label: string;
  totalRevenue: number;
  cancelled: number;
}

export interface RevenueBreakdown {
  key: string;
  productId: number;
  productType: string;
  venueId: number;
  totalRevenue: number;
  cancelled: number;
}

export interface DashboardRevenueData {
  buckets: RevenuePeriod[];
  breakdown: RevenueBreakdown[];
}

export interface ExpiringBooking {
  transaction: DashboardTransaction;
  service: string;
  venue: string;
  endDate: string;
  daysRemaining: number;
}

export interface DailyDashboard {
  date: string;
  months: RevenuePeriod[];
  expiries: ExpiringBooking[];
}

export const statusKey = (value: string): string => String(value || '').toLowerCase().replace(/\s+/g, '');
export const money = (value: number): number => Math.round((Number(value) || 0) * 100) / 100;
export const indiaDate = (value: string | number | Date) => moment(value).utcOffset('+05:30');
export const slotsOf = (row: DashboardTransaction): DashboardSlot[] =>
  Object.values(row.slotsBooked || {}).reduce((all, group) => all.concat(group), []);
const uniqueRows = (rows: DashboardTransaction[]) => Array.from(new Map(rows.map(row => [row.bookingId, row])).values());
const hasTransactionId = (row: DashboardTransaction) => !['', 'na', 'n/a', 'null', 'undefined', '-'].includes(statusKey(row.transactionId));
const successful = (value: string) => ['success', 'paid', 'successful', 'completed', 'txn_success'].includes(statusKey(value));
const failed = (value: string) => ['failed', 'failure', 'txn_failure'].includes(statusKey(value));
export const isPending = (row: DashboardTransaction) => ['waitingforpayment', 'waitingforpayments'].includes(statusKey(row.bookingStatus));

export function customerAmount(row: DashboardTransaction): number {
  return Math.max(0, money(Number(row.totalAmount || 0) + Number(row.totalPlatformFeeEndUser || 0) - Number(row.discountAmount || 0)));
}

export function isCollected(row: DashboardTransaction): boolean {
  if (isPending(row) || ['cancelled', 'canceled', 'failed'].includes(statusKey(row.bookingStatus))
    || failed(row.paymentStatus) || failed(row.transactionStatus)) return false;
  if (hasTransactionId(row)) return successful(row.paymentStatus) || successful(row.transactionStatus);
  return statusKey(row.paymentType) === 'cash' && statusKey(row.bookingStatus) === 'booked'
    && (['', 'na'].includes(statusKey(row.paymentStatus)) || successful(row.paymentStatus));
}

export function buildDailyDashboard(transactions: DashboardTransaction[], now = new Date()): DailyDashboard {
  const today = indiaDate(now).startOf('day');
  const rows = uniqueRows(transactions);
  const months: RevenuePeriod[] = Array.from({ length: 6 }, (_, index) => {
    const date = today.clone().startOf('month').subtract(5 - index, 'months');
    return { key: date.format('YYYY-MM'), label: date.format('MMM YY'), totalRevenue: 0, cancelled: 0 };
  });
  rows.filter(isCollected).forEach(row => {
    const date = indiaDate(row.bookingDateTime);
    if (!date.isValid() || date.isAfter(indiaDate(now))) return;
    const month = months.find(item => item.key === date.format('YYYY-MM'));
    if (month) month.totalRevenue = money(month.totalRevenue + customerAmount(row));
  });
  const expiries = new Map<string, ExpiringBooking>();
  rows.filter(row => statusKey(row.bookingStatus) === 'booked').forEach(row => {
    slotsOf(row).filter(slot => statusKey(slot.status) === 'booked').forEach(slot => {
      if (!slot.endDate || !indiaDate(slot.endDate).isValid()) return;
      const daysRemaining = indiaDate(slot.endDate).startOf('day').diff(today, 'days');
      if (daysRemaining < 0 || daysRemaining > 7) return;
      const endDate = indiaDate(slot.endDate).format('YYYY-MM-DD');
      const key = [row.bookingId, slot.productName, slot.venueName, endDate].join('|');
      expiries.set(key, { transaction: row, service: slot.productName, venue: slot.venueName, endDate, daysRemaining });
    });
  });
  return {
    date: today.format('YYYY-MM-DD'), months,
    expiries: Array.from(expiries.values()).sort((first, second) => first.daysRemaining - second.daysRemaining)
  };
}

export function buildDashboard(transactions: DashboardTransaction[], now = new Date(), daily?: DailyDashboard) {
  const today = indiaDate(now).startOf('day');
  const todayKey = today.format('YYYY-MM-DD');
  const rows = uniqueRows(transactions);
  const todayRows = rows.filter(row => indiaDate(row.bookingDateTime).format('YYYY-MM-DD') === todayKey
    && !indiaDate(row.bookingDateTime).isAfter(indiaDate(now)));
  const cash = todayRows.filter(row => isCollected(row) && !hasTransactionId(row));
  const online = todayRows.filter(row => isCollected(row) && hasTransactionId(row));
  const amount = (items: DashboardTransaction[]) => money(items.reduce((sum, row) => sum + customerAmount(row), 0));
  const since = today.clone().subtract(29, 'days');
  const pending = rows.filter(row => {
    const date = indiaDate(row.bookingDateTime);
    return isPending(row) && date.isValid() && !date.isBefore(since) && !date.isAfter(indiaDate(now));
  }).sort((first, second) => indiaDate(first.bookingDateTime).valueOf() - indiaDate(second.bookingDateTime).valueOf());
  return {
    ...(daily?.date === todayKey ? daily : buildDailyDashboard(rows, now)),
    pending,
    todayCount: todayRows.length,
    todayAmount: money(amount(cash) + amount(online)),
    cashCount: cash.length, cashAmount: amount(cash),
    onlineCount: online.length, onlineAmount: amount(online),
    failedCount: todayRows.filter(row => failed(row.paymentStatus) || failed(row.transactionStatus) || failed(row.bookingStatus)).length,
    pendingAmount: amount(rows.filter(isPending))
  };
}
